import {
  Checkbox,
  ChoiceChip,
  ChoiceChipGroup,
  DatePicker,
  Typography
} from '@sanlam-glacier/app-common/components';
import styles from './statement-period-selection.module.scss';
import { Control, Controller } from 'react-hook-form';
import { PeriodPresetOptions, StatementPeriodTypeOptions } from 'modules/clients/types/client.enum';
import {
  handleStatementPeriodToggle,
  validateDate
} from 'modules/clients/utils/statement-form-helper';
import { StatementFormValues } from 'modules/clients/types/client.types';

type MultiSelectStatementPeriodProps = {
  control: Control<StatementFormValues, StatementFormValues, StatementFormValues>;
  min30DaysStart: Date;
  errorState: boolean;
  setErrorState: React.Dispatch<React.SetStateAction<boolean>>;
};
export const MultiSelectStatementPeriod = ({
  control,
  min30DaysStart,
  errorState,
  setErrorState
}: MultiSelectStatementPeriodProps) => {
  return (
    <>
      <Typography
        variant={Typography.VARIANT.H5}
        weight={Typography.WEIGHT.MEDIUM}
        color={Typography.COLOR.DEFAULT}
      >
        Statement Period
      </Typography>
      <Typography
        variant={Typography.VARIANT.B3}
        weight={Typography.WEIGHT.NORMAL}
        color={Typography.COLOR.INHERIT}
        className={styles.subHeading}
      >
        Select standard periods <span className={styles.bold}>and/or</span> set a custom period (at
        least 30 days).
      </Typography>

      <div className={styles.checkboxContainer}>
        <Controller
          name='statementPeriodType'
          control={control}
          render={({ field }) => (
            <>
              <Checkbox
                aria-label={StatementPeriodTypeOptions.standardPeriods}
                onChange={(val) =>
                  handleStatementPeriodToggle(
                    val,
                    StatementPeriodTypeOptions.standardPeriods,
                    field
                  )
                }
              >
                Standard periods
              </Checkbox>

              <Controller
                name='periodPreset'
                control={control}
                render={({ field: chipField }) => (
                  <ChoiceChipGroup
                    multiSelect
                    className={styles.choiceChipGroup}
                    onChange={(_e, val) => chipField.onChange(val)}
                    disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                    assistiveText='Select one or multiple options'
                  >
                    <ChoiceChip
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                      aria-label={PeriodPresetOptions.yearToDate}
                      value={PeriodPresetOptions.yearToDate}
                    >
                      Year to date
                    </ChoiceChip>
                    <ChoiceChip
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                      aria-label={PeriodPresetOptions.sixMonths}
                      value={PeriodPresetOptions.sixMonths}
                    >
                      6 months
                    </ChoiceChip>
                    <ChoiceChip
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                      aria-label={PeriodPresetOptions.oneYear}
                      value={PeriodPresetOptions.oneYear}
                    >
                      1 year
                    </ChoiceChip>
                    <ChoiceChip
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                      aria-label={PeriodPresetOptions.threeYears}
                      value={PeriodPresetOptions.threeYears}
                    >
                      3 years
                    </ChoiceChip>
                    <ChoiceChip
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.standardPeriods)}
                      aria-label={PeriodPresetOptions.fiveYears}
                      value={PeriodPresetOptions.fiveYears}
                    >
                      5 years
                    </ChoiceChip>
                  </ChoiceChipGroup>
                )}
              />

              <Checkbox
                aria-label={StatementPeriodTypeOptions.customPeriod}
                onChange={(val) =>
                  handleStatementPeriodToggle(val, StatementPeriodTypeOptions.customPeriod, field)
                }
              >
                Custom period
              </Checkbox>

              <Controller
                name='dateRange'
                control={control}
                render={({ field: datePickerField }) => (
                  <div className={styles.datePickerContainer}>
                    <DatePicker
                      date={datePickerField.value[0] as Date}
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.customPeriod)}
                      assistiveText='Minimum 30 days'
                      controlled
                      dateFormat='dd/MM/yyyy'
                      label='Select date from'
                      maxDate={min30DaysStart}
                      minDate={new Date('2015-09-30T09:32:41.201Z')}
                      onChange={(date) => {
                        datePickerField.onChange([date as Date, datePickerField.value[1]]);
                      }}
                      orderByLatestYear
                      selectsStart={true}
                      shouldCloseOnSelect
                      error={errorState}
                    />
                    <DatePicker
                      date={datePickerField.value[1] as Date}
                      disabled={!field.value?.includes(StatementPeriodTypeOptions.customPeriod)}
                      controlled
                      dateFormat='dd/MM/yyyy'
                      label='Select date to'
                      maxDate={new Date()}
                      minDate={new Date('2015-09-30T09:32:41.201Z')}
                      onChange={(date) => {
                        datePickerField.onChange([datePickerField.value[0], date as Date]);
                        if (
                          !(
                            datePickerField.value[0] !== null &&
                            validateDate(datePickerField.value[0], date as Date)
                          )
                        ) {
                          setErrorState(true);
                        }
                      }}
                      orderByLatestYear
                      selectsEnd
                      shouldCloseOnSelect
                      error={errorState}
                    />
                  </div>
                )}
              />
            </>
          )}
        />
      </div>
    </>
  );
};
